'use client'

import EditableItem from '#/components/cms/EditableItem'
import EditablePageHeader from '#/components/cms/EditablePageHeader'
import type { EditField } from '#/components/cms/EditModal'
import Container from '#/components/layout/Container'
import Section from '#/components/layout/Section'
import { BlurFade } from '#/components/ui/blur-fade'
import { Button } from '#/components/ui/button'
import { Input } from '#/components/ui/input'
import { Label } from '#/components/ui/label'
import { Textarea } from '#/components/ui/textarea'
import { getServicesInfo } from '#/lib/content'
import { useCmsContent } from '#/hooks/useCmsContent'
import type { ServiceInfo } from '#/lib/content-types'
import { submitServicesForm } from '#/lib/server-actions'
import { servicesFormSchema } from '#/lib/form-schemas'
import { createFileRoute } from '@tanstack/react-router'
import { iconMap, fallbackIcon } from '#/lib/icons'
import { Loader2, Send } from 'lucide-react'

import { useState } from 'react'
import { toast } from 'sonner'

const serviceFields: EditField[] = [
  { name: 'title', type: 'text', label: 'Title' },
  { name: 'description', type: 'textarea', label: 'Description' },
  { name: 'icon', type: 'icon', label: 'Icon' },
]


import { seoMeta, canonicalLink } from '#/lib/seo'

export const Route = createFileRoute('/services')({
  head: () => ({
    meta: seoMeta({
      title: 'Services & Partnerships',
      description:
        'Wholesale bread orders, event catering, and local business partnerships with Main Street Bread in Ferndale, MI.',
      path: '/services',
    }),
    links: [canonicalLink('/services')],
  }),
  loader: async () => {
    const services = await getServicesInfo()
    return { services }
  },
  component: ServicesPage,
})

function ServicesPage() {
  const data = Route.useLoaderData()
  const services = useCmsContent('services-info', data.services)
  const [formState, setFormState] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    serviceType: '',
    message: '',
  })
  const [honeypot, setHoneypot] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  function updateField<K extends keyof typeof formState>(key: K, value: string) {
    setFormState((s) => ({ ...s, [key]: value }))
    if (fieldErrors[key]) {
      setFieldErrors((e) => ({ ...e, [key]: undefined }))
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const parsed = servicesFormSchema.safeParse({ ...formState, website: honeypot })
    if (!parsed.success) {
      const flat = parsed.error.flatten().fieldErrors
      const next: Record<string, string | undefined> = {}
      for (const [k, v] of Object.entries(flat)) next[k] = v?.[0]
      setFieldErrors(next)
      return
    }
    setFieldErrors({})
    setSubmitting(true)
    try {
      await submitServicesForm({ data: parsed.data })
      setSubmitted(true)
    } catch (err) {
      toast.error(
        err instanceof Error && err.message
          ? err.message
          : 'Something went wrong. Please try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main>
      {/* Header */}
      <Section tone="surface" className="py-12 sm:py-16">
        <Container>
          <EditablePageHeader page="services" />
        </Container>
      </Section>

      {/* Services Grid */}
      <Section tone="low">
        <Container>
          <div className="grid gap-6 sm:grid-cols-2">
            {services.map((service: ServiceInfo, i: number) => {
              const Icon = iconMap[service.icon] ?? fallbackIcon
              return (
                <BlurFade key={service.id} delay={0.1 * i}>
                  <EditableItem
                    file="services-info"
                    itemId={service.id}
                    fields={serviceFields}
                    values={{ title: service.title, description: service.description, icon: service.icon }}
                    title={`Edit: ${service.title}`}
                  >
                    <div
                      className="rounded-lg bg-surface-lowest p-6 transition-all duration-200 hover:-translate-y-1"
                      style={{ boxShadow: 'var(--shadow-ambient)' }}
                      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = 'var(--shadow-elevated)')}
                      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'var(--shadow-ambient)')}
                    >
                      <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-gradient-to-br from-primary-container/15 to-tertiary-container/10">
                        <Icon className="size-5 text-primary-container" />
                      </div>
                      <h3 className="mb-2 font-serif text-lg font-bold text-on-surface">
                        {service.title}
                      </h3>
                      <p className="whitespace-pre-line text-sm leading-relaxed text-on-surface-variant">
                        {service.description}
                      </p>
                    </div>
                  </EditableItem>
                </BlurFade>
              )
            })}
          </div>
        </Container>
      </Section>

      {/* Inquiry Form */}
      <Section tone="surface">
        <Container className="max-w-2xl">
          <div className="mb-8 text-center">
            <p className="kicker mb-2">Get in Touch</p>
            <h2 className="font-serif text-2xl font-bold text-on-surface sm:text-3xl">
              Business Inquiry
            </h2>
          </div>

          {submitted ? (
            <div className="rounded-lg bg-surface-lowest p-8 text-center" style={{ boxShadow: 'var(--shadow-ambient)' }}>
              <p className="font-serif text-xl font-semibold text-on-surface">Thank you!</p>
              <p className="mt-2 text-on-surface-variant">
                We&apos;ll review your inquiry and get back to you within 2 business days.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5 rounded-lg bg-surface-lowest p-6 py-8 sm:p-8 sm:py-10" style={{ boxShadow: 'var(--shadow-ambient)' }}>
              {/* Honeypot */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                aria-hidden="true"
                className="sr-only"
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="businessName">Business Name</Label>
                  <Input
                    id="businessName"
                    value={formState.businessName}
                    onChange={(e) => updateField('businessName', e.target.value)}
                    aria-invalid={!!fieldErrors.businessName}
                    className="bg-surface-highest"
                  />
                  {fieldErrors.businessName && (
                    <p className="mt-1 text-xs text-destructive">{fieldErrors.businessName}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="contactName">Contact Name</Label>
                  <Input
                    id="contactName"
                    value={formState.contactName}
                    onChange={(e) => updateField('contactName', e.target.value)}
                    aria-invalid={!!fieldErrors.contactName}
                    className="bg-surface-highest"
                  />
                  {fieldErrors.contactName && (
                    <p className="mt-1 text-xs text-destructive">{fieldErrors.contactName}</p>
                  )}
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="svc-email">Email</Label>
                  <Input
                    id="svc-email"
                    type="email"
                    value={formState.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    aria-invalid={!!fieldErrors.email}
                    className="bg-surface-highest"
                  />
                  {fieldErrors.email && (
                    <p className="mt-1 text-xs text-destructive">{fieldErrors.email}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="svc-phone">Phone</Label>
                  <Input
                    id="svc-phone"
                    type="tel"
                    value={formState.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    className="bg-surface-highest"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="serviceType">Service Type</Label>
                <select
                  id="serviceType"
                  value={formState.serviceType}
                  onChange={(e) => updateField('serviceType', e.target.value)}
                  aria-invalid={!!fieldErrors.serviceType}
                  className="w-full rounded-md bg-surface-highest px-3 py-2 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">Select a service...</option>
                  <option value="wholesale">Wholesale Partnerships</option>
                  <option value="catering">Event Catering</option>
                  <option value="custom">Custom Orders</option>
                  <option value="partnership">Local Business Partnership</option>
                </select>
                {fieldErrors.serviceType && (
                  <p className="mt-1 text-xs text-destructive">{fieldErrors.serviceType}</p>
                )}
              </div>
              <div>
                <Label htmlFor="svc-message">Message</Label>
                <Textarea
                  id="svc-message"
                  rows={5}
                  value={formState.message}
                  onChange={(e) => updateField('message', e.target.value)}
                  aria-invalid={!!fieldErrors.message}
                  placeholder="Tell us about your business and how we can work together..."
                  className="bg-surface-highest"
                />
                {fieldErrors.message && (
                  <p className="mt-1 text-xs text-destructive">{fieldErrors.message}</p>
                )}
              </div>
              <Button type="submit" disabled={submitting} className="w-full rounded-full bg-primary hover:bg-primary-container text-white">
                {submitting ? (
                  <Loader2 className="mr-2 size-4 animate-spin" />
                ) : (
                  <Send className="mr-2 size-4" />
                )}
                {submitting ? 'Submitting...' : 'Submit Inquiry'}
              </Button>
            </form>
          )}
        </Container>
      </Section>
    </main>
  )
}
